import { lazy, Suspense } from "react"
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom"
import { ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import { SidebarProvider, useSidebar } from "./context/SidebarContext"
import { PublicRoute, ProtectedRoute, RouteGuard, ForbiddenPage } from "./components/ProtectedRoute"
import AuthLogoutHandler from "./components/AuthLogoutHandler"
import ErrorBoundary, { RouteErrorBoundary } from "./components/ErrorBoundary"
import Spinner from "./components/ui/Spinner"
import { loginApi } from "./api/loginApi"

// Public Pages
import CompanyLogin from "./pages/CompanyLogin"
import CompanyForgotPassword from "./pages/CompanyForgotPassword"

// Authenticated pages and their paths
import { PROTECTED_ROUTES } from "./navigation/protectedRoutes"
import PageLoader from "./navigation/PageLoader"
import { LOGIN_PATH, FORGOT_PASSWORD_PATH, FORBIDDEN_PATH, HOME_PATH, UI_PLAYGROUND_PATH } from "./navigation/paths"

// Development-only UI playground. The condition is resolved at build time, so production
// builds contain neither the route nor the playground code.
const UiPlayground = process.env.NODE_ENV === "development" ? lazy(() => import("./dev/UiPlayground")) : null

/**
 * APP BOOTSTRAP COMPONENT
 * =======================
 * Handles initial app loading state.
 * Shows loader while sidebar is being fetched.
 */
function AppBootstrap({ children }) {
  const { loading, error } = useSidebar()
  const isAuthenticated = loginApi.isAuthenticated()

  // If not authenticated, don't show loader (let routes handle redirect)
  if (!isAuthenticated) {
    return children
  }

  // Show loader while fetching sidebar
  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <Spinner size="lg" showLabel label="Loading system menu…" />
      </div>
    )
  }

  // Show error state if sidebar failed to load
  if (error) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
        <div className="text-center">
          <div className="text-danger mb-3">
            <i className="fe fe-alert-circle" style={{ fontSize: "3rem" }} aria-hidden="true"></i>
          </div>
          <h4 className="text-danger" role="alert">Unable to Load System Menu</h4>
          <p className="text-muted">{error}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              loginApi.logout()
              window.location.href = LOGIN_PATH
            }}
          >
            Return to Login
          </button>
        </div>
      </div>
    )
  }

  return children
}

/**
 * MAIN APP COMPONENT
 * ==================
 * Wraps application in providers and defines routes.
 */
function AppRoutes() {
  return (
    <AppBootstrap>
      <AuthLogoutHandler />
      <RouteErrorBoundary>
      <Routes>
        {/* Public Routes */}
        <Route path={LOGIN_PATH} element={<PublicRoute element={<CompanyLogin />} />} />
        <Route path={FORGOT_PASSWORD_PATH} element={<PublicRoute element={<CompanyForgotPassword />} />} />

        {/* 403 Forbidden Page */}
        <Route path={FORBIDDEN_PATH} element={<ForbiddenPage />} />

        {/* Authenticated pages (see navigation/protectedRoutes.js), code-split per page.
            Suspense sits inside ProtectedRoute so the auth check still runs before any page code loads. */}
        {PROTECTED_ROUTES.map(({ path, Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Component />
                  </Suspense>
                }
              />
            }
          />
        ))}

        {/* Development-only UI playground (not in the sidebar, not in production builds) */}
        {UiPlayground && (
          <Route
            path={UI_PLAYGROUND_PATH}
            element={
              <Suspense fallback={null}>
                <UiPlayground />
              </Suspense>
            }
          />
        )}

        {/* Redirect Root → Dashboard */}
        <Route path="/" element={<Navigate to={HOME_PATH} replace />} />

        {/* Redirect Unknown Routes → Dashboard */}
        <Route path="*" element={<Navigate to={HOME_PATH} replace />} />
      </Routes>
      </RouteErrorBoundary>
    </AppBootstrap>
  )
}

/**
 * ROOT APP COMPONENT
 * ==================
 * Wraps everything in Router and SidebarProvider
 */
export default function App() {
  return (
    <Router>
      {/* App-level safety net (e.g. a failure in providers or the bootstrap loader) */}
      <ErrorBoundary fullPage title="Something went wrong" message="The dashboard hit an unexpected error. Please try again.">
        <SidebarProvider>
          <AppRoutes />
          <ToastContainer />
        </SidebarProvider>
      </ErrorBoundary>
    </Router>
  )
}
